import {useEffect, useRef} from "react";
import {ACCESS_MONITORING_SERVICE} from "../../../../../reduxToolkit/EndPoints";
import {Client} from "@stomp/stompjs";
import SockJS from "sockjs-client";
import {guardURL} from "../../../../../Apis/Constants";
import {ACTION_MODEL} from "../../../../../Helpers/ActionModel";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * DailyAccessMonitoringWebSocket Hook
 *
 * This custom hook manages the complete WebSocket lifecycle for the
 * daily access monitoring module.
 *
 * It is responsible for:
 *
 * - Establishing STOMP WebSocket connections
 * - Handling automatic reconnection logic
 * - Managing subscriptions safely
 * - Publishing monitoring filters to the backend
 * - Receiving real-time employee monitoring updates
 * - Synchronizing online/offline connection state
 * - Cleaning resources and sockets on unmount
 * - Republishing monitoring filters when they change
 *
 * The hook also protects against stale closures using refs and prevents
 * duplicated reconnection attempts during connection failures.
 *
 * @param {Object} props
 * @param {Object} props.item - Current monitoring filter payload sent to the backend.
 * @param {boolean} props.isOnline - Indicates whether the WebSocket connection is active.
 * @param {Function} props.setIsOnline - Updates the online/offline connection state.
 * @param {Function} props.setActiveZoneOptions - Updates available active zone options.
 * @param {Function} props.setEmployees - Updates the employee monitoring dataset.
 *
 * @returns {Object} Object containing:
 *
 * @returns {Function} reconnectWebSocket - Manually forces a WebSocket reconnection attempt.
 */
export const DailyAccessMonitoringWebSocket  = ({
                                                    item,
                                                    isOnline,
                                                    setIsOnline,
                                                    setActiveZoneOptions,
                                                    setEmployees
                                                }) => {
    const subscriptionsRef = useRef([]);
    const itemRef = useRef(item);
    const clientRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const connectionTimeoutRef = useRef(null);
    const isReconnectingRef = useRef(false);
    const connectionSuccessRef = useRef(false);
    const isMountedRef = useRef(false);

    /**
     * Sync latest item into ref to avoid stale closures
     */
    useEffect(() => {
        itemRef.current = item;
    }, [item]);

    /**
     * Updates monitoring UI data received from
     * the WebSocket stream.
     *
     * <p>
     * Updates:
     * </p>
     *
     * <ul>
     *   <li>Available active zones</li>
     *   <li>Employee monitoring dataset</li>
     * </ul>
     *
     * @param {Object} data WebSocket payload.
     * @param {Array} [data.zones] Available monitoring zones.
     * @param {Object} [data.employees] Paginated employee dataset.
     * @param {Array} [data.employees.content] Employee row collection.
     *
     * @returns {void}
     */
    const handleSetData = (data) => {
        setActiveZoneOptions(data?.zones ?? []);
        setEmployees(data?.employees);
    };

    /**
     * Publishes the current monitoring filters
     * to the backend topic.
     *
     * <p>
     * The request is only sent when:
     * </p>
     *
     * <ul>
     *   <li>The client exists</li>
     *   <li>The socket connection is active</li>
     *   <li>The monitoring payload exists</li>
     * </ul>
     *
     * @returns {void}
     */
    const publishDailyAccessMonitoring = () => {
        const client = clientRef.current;
        if (!client?.connected || !itemRef.current) return;

        client.publish({
            destination: ACCESS_MONITORING_SERVICE.publishDailyAccessMonitoring,
            body: JSON.stringify(itemRef.current)
        });
    };

    /**
     * Completely destroys the current WebSocket connection.
     *
     * <p>
     * Responsibilities include:
     * </p>
     *
     * <ul>
     *   <li>Clearing reconnect timeouts</li>
     *   <li>Clearing connection validation timeouts</li>
     *   <li>Unsubscribing active subscriptions</li>
     *   <li>Deactivating the STOMP client</li>
     *   <li>Closing the underlying WebSocket</li>
     *   <li>Resetting connection state references</li>
     * </ul>
     *
     * @returns {Promise<void>}
     */
    const destroyWebSocket = async () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
        }

        subscriptionsRef.current.forEach((sub) => {
            try {
                sub.unsubscribe();
            } catch (e) {}
        });
        subscriptionsRef.current = [];

        const client = clientRef.current;
        if (!client) {
            isReconnectingRef.current = false;
            connectionSuccessRef.current = false;
            return;
        }

        try {
            await client.deactivate();
        } catch (e) {}

        try {
            if (client.webSocket) client.webSocket.close();
            else if (client._webSocket) client._webSocket.close();
        } catch (e) {}

        clientRef.current = null;
        isReconnectingRef.current = false;
        connectionSuccessRef.current = false;
    };

    /**
     * Schedules a future reconnect attempt.
     *
     * <p>
     * Prevents duplicated reconnect scheduling
     * while another reconnect process is active.
     * </p>
     *
     * @param {number} [delay=5000] Delay in milliseconds before reconnecting.
     *
     * @returns {void}
     */
    const scheduleReconnect = (delay = 5000) => {
        if (!isMountedRef.current) return;
        if (reconnectTimeoutRef.current || isReconnectingRef.current) return;

        isReconnectingRef.current = true;

        reconnectTimeoutRef.current = setTimeout(async () => {
            reconnectTimeoutRef.current = null;

            if (!isMountedRef.current) {
                isReconnectingRef.current = false;
                return;
            }

            await destroyWebSocket();
            connectWebSocket();
        }, delay);
    };

    /**
     * Initializes and activates the STOMP WebSocket connection.
     *
     * <p>
     * Handles:
     * </p>
     *
     * <ul>
     *   <li>Socket connection lifecycle</li>
     *   <li>Subscription registration</li>
     *   <li>Incoming monitoring messages</li>
     *   <li>Refresh event handling</li>
     *   <li>Automatic reconnect scheduling</li>
     *   <li>Connection validation timeout</li>
     * </ul>
     *
     * <p>
     * The connection automatically republishes the current
     * monitoring filters after a successful connection.
     * </p>
     *
     * @returns {void}
     */
    const connectWebSocket = () => {
        if (!isMountedRef.current) return;
        if (clientRef.current?.active || clientRef.current?.connected) return;

        const client = new Client({
            webSocketFactory: () => new SockJS(guardURL),
            debug: () => {},
            reconnectDelay: 0,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,

            onConnect: () => {
                if (!isMountedRef.current) return;

                setIsOnline(true);
                connectionSuccessRef.current = true;
                isReconnectingRef.current = false;

                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                subscriptionsRef.current.forEach((sub) => {
                    try {
                        sub.unsubscribe();
                    } catch (e) {}
                });
                subscriptionsRef.current = [];

                const subscription = client.subscribe(
                    ACCESS_MONITORING_SERVICE.topicDailyAccessMonitoring,
                    (message) => {
                        let parsed;

                        try {
                            parsed = JSON.parse(message.body);
                        } catch (e) {
                            return;
                        }

                        if (parsed?.action === ACTION_MODEL.REFRESH) {
                            publishDailyAccessMonitoring();
                            return;
                        }

                        if (parsed?.data) {
                            handleSetData(parsed.data);
                        }
                    }
                );

                subscriptionsRef.current = [subscription];
                publishDailyAccessMonitoring();
            },

            onStompError: () => {


                if (!isMountedRef.current) return;

                setIsOnline(false);
                connectionSuccessRef.current = false;
                scheduleReconnect();
            },

            onWebSocketClose: () => {
                if (!isMountedRef.current) return;

                setIsOnline(false);
                connectionSuccessRef.current = false;
                scheduleReconnect();
            },

            onWebSocketError: () => {
                if (!isMountedRef.current) return;

                setIsOnline(false);
                connectionSuccessRef.current = false;
                scheduleReconnect();
            }
        });

        clientRef.current = client;
        client.activate();

        connectionTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;

            const currentClient = clientRef.current;
            if (currentClient === client && !client.connected && !connectionSuccessRef.current) {
                await destroyWebSocket();
                scheduleReconnect(4000);
            }
        }, 10000);
    };

    /**
     * Handles component mount and unmount lifecycle.
     *
     * <p>
     * On mount:
     * </p>
     *
     * <ul>
     *   <li>Marks the hook as mounted</li>
     *   <li>Initializes the WebSocket connection</li>
     * </ul>
     *
     * <p>
     * On unmount:
     * </p>
     *
     * <ul>
     *   <li>Marks the hook as unmounted</li>
     *   <li>Destroys all WebSocket resources</li>
     * </ul>
     */
    useEffect(() => {
        isMountedRef.current = true;
        connectWebSocket();

        return () => {
            isMountedRef.current = false;
            destroyWebSocket();
        };
    }, []);

    /**
     * Republishes monitoring filters whenever
     * the filter payload changes.
     *
     * <p>
     * Republishing only occurs when the
     * WebSocket connection is active.
     * </p>
     */
    useEffect(() => {
        if (clientRef.current?.connected && item) {
            publishDailyAccessMonitoring();
        }
    }, [item]);

    /**
     * Manually forces a WebSocket reconnection attempt.
     *
     * <p>
     * The reconnect process is ignored when
     * the socket is already connected.
     * </p>
     *
     * @returns {Promise<void>}
     */
    const reconnectWebSocket = async () => {
        if (isOnline || clientRef.current?.connected) return;

        await destroyWebSocket();
        connectWebSocket();
    };

    return { reconnectWebSocket };
};
